/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { mapProps } from 'recompose';
import { get } from 'lodash';
import t from 'app/utils/translate';
import LightSweepButton from 'app/components/commons/LightSweepButton';
import ButtonWrapper from './ChallengeButtonWrapper';
import { getChallengeEntryStatus } from './ChallengeEntryState';

type OuterProps = {
  entryCap: number,
  entryCount: number,
  isUnlimited: boolean,
  maxEntriesPerPerson: number,
  statusId: number,
  userEntryCount: number
};

type InnerProps = {
  hasBorder?: boolean,
  component: any,
  text?: string,
  textColor?: string
};

const EnterButtonWrapper = styled(ButtonWrapper)(({ theme }) => ({
  minWidth: 110,
  position: 'relative',
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    paddingLeft: theme.spacing.small,
    paddingRight: theme.spacing.small
  },
  [theme.mediaQuery.tabletPortraitAndBelow]: {
    fontSize: '1rem'
  },
  [theme.mediaQuery.mobileOnly]: {
    display: 'flex',
    justifyContent: 'flex-start',
    minWidth: 90,
    fontSize: '0.9rem',
    paddingLeft: theme.spacing.xsmall,
    paddingRight: theme.spacing.xsmall
  }
}));

const Border = styled.div(
  {
    position: 'absolute',
    height: '100%',
    left: 0,
    transform: 'scale(-1, 0.7)',
    width: 1
  },
  ({ theme }) => ({
    backgroundColor: theme.color.basis6
  })
);

const EnterButton = styled(LightSweepButton)(({ theme }) => ({
  width: '100%',
  background: get(theme.color, 'enterButton', theme.color.info),
  color: theme.color.white,
  ':hover': {
    background: get(theme.color, 'enterButton', theme.color.info)
  }
}));

const StaticButton = styled.div(({ theme }) => ({
  lineHeight: '32px',
  fontWeight: 600,
  position: 'relative',
  textAlign: 'center',
  borderRadius: 2,
  width: '100%',
  background: theme.color.white,
  border: `2px solid ${get(theme.color, 'enterButton', theme.color.info)}`,
  color: theme.color.black,
  '&::before': {
    display: 'inline-block',
    fontFamily: 'Ionicons',
    fontSize: 19
  },
  [theme.mediaQuery.mobileOnly]: {
    lineHeight: '22px',
    '&::before': {
      fontSize: 16,
      verticalAlign: 'middle'
    }
  }
}));

const LockedButton = styled(StaticButton)({
  '&::before': {
    content: '"\\f200"'
  }
});

const EnteredButton = styled(StaticButton)(({ theme }) => ({
  color: get(theme.color, 'enteredButton', theme.color.success),
  border: `2px solid ${get(theme.color, 'enteredButton', theme.color.success)}`,
  '&::before': {
    content: '"\\f120"'
  }
}));

const EntryText = styled.span(({ theme, color }) => ({
  color: color ? theme.color[color] : theme.color.black,
  fontSize: 16,
  [theme.mediaQuery.mobileOnly]: {
    fontSize: 14,
    lineHeight: '14px',
    verticalAlign: 'middle'
  }
}));

function propsMapper({
  entryCap,
  entryCount,
  isUnlimited,
  maxEntriesPerPerson,
  statusId,
  userEntryCount
}: OuterProps): InnerProps {
  const status = getChallengeEntryStatus({
    maxEntriesPerPerson,
    userEntryCount,
    statusId,
    entryCap,
    entryCount,
    isUnlimited
  });

  switch (status) {
    case 'entered':
      return {
        component: EnteredButton
      };

    case 'multi_entered':
      return {
        component: EnteredButton,
        text: `  ${userEntryCount}/${maxEntriesPerPerson}`
      };

    case 'open':
      return {
        component: EnterButton,
        text: t('Enter'),
        textColor: 'white'
      };

    default:
      return {
        component: LockedButton
      };
  }
}

const defaultProps = {
  hasBorder: false,
  text: undefined,
  textColor: undefined
};

function ChallengeEnterButton(props: InnerProps) {
  const { component: Component, text, textColor, hasBorder } = props;

  return (
    <EnterButtonWrapper>
      {hasBorder && <Border />}
      <Component>{text && <EntryText color={textColor}>{text}</EntryText>}</Component>
    </EnterButtonWrapper>
  );
}

ChallengeEnterButton.defaultProps = defaultProps;

export default mapProps(propsMapper)(ChallengeEnterButton);
